import React, { memo, Fragment, useEffect, useCallback } from "react";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import AxiomLoader from "__AxiomComponents/AxiomUI/AxiomLoader/AxiomLoader.jsx";

import Icon from "../Icons/Icon";
import HawkeyeV2CreateFilter from "../HawkeyeV2CreateFilter/HawkeyeV2CreateFilter";
import HawkeyeV2EditFilter from "../HawkeyeV2EditFilter/HawkeyeV2EditFilter";
import HawkeyeV2EditFilterPanel from "../HawkeyeV2EditFilter/HawkeyeV2EditFilterPanel";
import HawkeyeV2FilterInfo from "../HawkeyeV2FilterInfo/HawkeyeV2FilterInfo";
import HawkeyeV2SavedFilters from "../HawkeyeV2SavedFilters/HawkeyeV2SavedFilters";
import HawkeyeV2StaticFilter from "../HawkeyeV2StaticFilter/HawkeyeV2StaticFilter";
import HawkeyeV2CreateFilterPanel from "../HawkeyeV2CreateFilterPanel/HawkeyeV2CreateFilterPanel";
import HawkeyeV2FilterPortal from "./HawkeyeV2FilterPortal";

import {
  getKPIWiseFilters,
  getAppliedFilters,
  getAppliedGloalFilters,
} from "./HawkeyeV2FilterUtils";

import "./HawkeyeV2Filters.scss";

function HawkeyeV2Filters(props) {
  const {
    widget,
    isFilterOpen,
    setKPIWiseFilters,
    selectedGlobalFilter,
    currentPage,
    setGlobalFilters,
    isRightSideFilterPanelRequired = false,
    HawkeyeV2AppSettings = {},
    KPIWiseConfigs,
    showPublicToggle = true,
  } = props;

  const [state, dispatch] = useAxiomState(
    {
      loading: false,
      filters: [],
      savedFilters: [],
      defaultFilter: null,
      selectedFilters: [],
      smartFilterRefs: null,
      widgetGlobalFilter: null,

      // Filter creation state variable
      isCreatingFilter: false,

      // Filter edit state variable
      isEditingFilter: false,
      editingFilter: null,

      // Filter view state variable
      isViewingInfo: false,
      infoFilter: null,
      enableEdit: true,
    },
    props.appName,
    props.appID,
    "HawkeyeV2Filters",
    props.componentID
  );

  // Get filter data on initial load
  useEffect(() => {
    if (widget.widgetSettings.KPIList) {
      dispatch(
        {
          widgetID: widget.widgetID,
          KPIList: widget.widgetSettings.KPIList,
          selectedFilters: widget.selectedFilters,
          widgetDefaultFilter: widget.widgetDefaultFilter,
        },
        "AxiomEvent",
        "getFilters"
      );
    }
  }, [dispatch, widget]);

  // Update KPIWiseFilters on filter selection change
  useEffect(() => {
    if (state.viewIDtoKPIMapping && KPIWiseConfigs) {
      const KPIWiseFilters = getKPIWiseFilters(
        state.selectedFilters,
        state.viewIDtoKPIMapping,
        KPIWiseConfigs
      );
      setKPIWiseFilters(KPIWiseFilters);
    }
  }, [state.selectedFilters, state.viewIDtoKPIMapping, setKPIWiseFilters, KPIWiseConfigs]);

  // Open create filter modal
  const openCreateFilter = useCallback(() => {
    document.body.classList.add("no-scroll");
    dispatch({ isCreatingFilter: true });
  }, [dispatch]);

  // Close create filter modal
  const closeCreateFilter = useCallback(() => {
    document.body.classList.remove("no-scroll");
    dispatch({ isCreatingFilter: false });
  }, [dispatch]);

  // Open edit filter modal
  const openEditFilter = useCallback(
    (editingFilter) => {
      document.body.classList.add("no-scroll");
      dispatch({ isEditingFilter: true, editingFilter });
    },
    [dispatch]
  );

  // Close edit filter modal
  const closeEditFilter = useCallback(() => {
    document.body.classList.remove("no-scroll");
    dispatch({ isEditingFilter: false, editingFilter: null });
  }, [dispatch]);

  // Open filter information modal
  const openShowInfo = useCallback(
    (infoFilter, enableEdit) => {
      document.body.classList.add("no-scroll");
      dispatch({ isViewingInfo: true, infoFilter: infoFilter, enableEdit: enableEdit });
    },
    [dispatch]
  );

  // Close filter information modal
  const closeShowInfo = useCallback(() => {
    document.body.classList.remove("no-scroll");
    dispatch({ isViewingInfo: false, infoFilter: null });
  }, [dispatch]);

  // Select or de-select a saved filter on current widget
  const applyFilter = useCallback(
    (filter) => {
      dispatch((state) => ({
        selectedFilters: getAppliedFilters(filter, state.selectedFilters, state.filters),
      }));
    },
    [dispatch]
  );

  // Save a new filter on server
  const saveFilter = useCallback(
    (saveData, isSaveFilterRequired = true) => {
      if (isSaveFilterRequired == true) {
        dispatch({
          loading: true,
        });
        dispatch({ saveData: saveData, currentPage: currentPage }, "AxiomEvent", "saveFilters");
      } else {
        const savedFiltersData = [...state.savedFilters];
        const filterData = {
          filterName: `CustomFilter-${savedFiltersData.length + 1}`,
          filterSettings: saveData.filters,
          isEditable: true,
          isPublic: 0,
          isSaveFilterRequired: false,
          filterID: `CustomFilter-${savedFiltersData.length + 1}`,
        };
        savedFiltersData.push(filterData);
        dispatch({
          savedFilters: savedFiltersData,
        });
        setTimeout(() => {
          applyFilter(filterData);
        }, 100);
      }
    },
    [dispatch, currentPage, state.savedFilters, applyFilter]
  );

  // Update a saved filter on server
  const updateSavedFilter = useCallback(
    (updateData, isSaveFilterRequired = true) => {
      if (isSaveFilterRequired == true) {
        dispatch({
          loading: true,
        });
        dispatch(
          { updateData: updateData, currentPage: currentPage },
          "AxiomEvent",
          "updateFilter"
        );
      } else {
        const savedFiltersData = [...state.savedFilters];
        let filterData = null;
        savedFiltersData.forEach((d) => {
          if (d.filterName == updateData.filterName) {
            d.filterSettings = updateData.filters;
            filterData = d;
          }
        });
        dispatch({
          savedFilters: savedFiltersData,
        });
        if (filterData != null) {
          const updatedFilter = getAppliedFilters(filterData, [], state.filters);
          setTimeout(() => {
            applyFilter(filterData);
          }, 100);
        }
      }
    },
    [dispatch, currentPage, state.savedFilters, applyFilter, state.filters]
  );

  // Edit a saved filter
  const editFilter = useCallback(
    (editingFilterID, filterName, editedFilters, isPublic, isSaveFilterRequired = true) => {
      dispatch((state) => {
        const { selectedFilters } = state;
        const editingFilterIndex = selectedFilters.findIndex((f) => f.filterID === editingFilterID);
        // If editing filter is selected then update it in selected filters as well
        if (editingFilterIndex > -1) {
          selectedFilters[editingFilterIndex].filterName = filterName;
          selectedFilters[editingFilterIndex].filterSettings = editedFilters;
          return { selectedFilters: [...selectedFilters] };
        } else {
          return null;
        }
      });
      updateSavedFilter(
        {
          widget: widget,
          filterID: editingFilterID,
          filterName: filterName,
          isPublic: isPublic,
          filters: editedFilters.map((f) => ({
            filter: {
              displayName: f.filter.displayName,
              key: f.filter.key,
            },
            values: f.values,
          })),
        },
        isSaveFilterRequired
      );
    },
    [dispatch, updateSavedFilter, widget]
  );

  // Delete a saved filter on server
  const deleteSavedFilter = useCallback(
    (filter) => {
      const confirmation = window.confirm("Are you sure you want to delete saved filter.");
      if (confirmation) {
        // If the filter being deleted is applied then remove it.
        dispatch((state) => {
          const { selectedFilters } = state;
          const selectedIndex = selectedFilters.findIndex((f) => f.filterID === filter.filterID);
          if (selectedGlobalFilter && selectedGlobalFilter.filterID == filter.filterID) {
            setGlobalFilters(selectedGlobalFilter);
          }
          if (selectedIndex > -1) {
            selectedFilters.splice(selectedIndex, 1);
            return { selectedFilters: [...selectedFilters] };
          } else {
            return null;
          }
        });
        if (filter.isSaveFilterRequired == false) {
          let savedFiltersData = [...state.savedFilters];
          savedFiltersData = savedFiltersData.filter((d) => {
            return d.filterName != filter.filterName;
          });
          dispatch({
            savedFilters: savedFiltersData,
            isEditingFilter: false,
            editingFilter: null,
          });
        } else {
          dispatch({
            loading: true,
            isEditingFilter: false,
            editingFilter: null,
          });
          dispatch(
            {
              widget,
              filterID: filter.filterID,
              currentPage: currentPage,
              isSaveFilterRequired: filter.isSaveFilterRequired,
            },
            "AxiomEvent",
            "deleteSavedFilter"
          );
        }
      }
    },
    [dispatch, widget, currentPage, selectedGlobalFilter, setGlobalFilters, state.savedFilters]
  );

  //Default filter apply
  useEffect(() => {
    if (widget.widgetDefaultFilter && widget.widgetDefaultFilter.filters && state.filters.length) {
      const filter = {
        filterName: widget.widgetDefaultFilter.name,
        filterSettings: widget.widgetDefaultFilter.filters,
      };
      dispatch({ defaultFilter: filter });
      dispatch((state) => ({
        selectedFilters: getAppliedFilters(filter, state.selectedFilters, state.filters),
      }));
    }
  }, [dispatch, widget, state.filters]);

  //Global filter apply
  useEffect(() => {
    if (state.filters.length) {
      let isApplicableToWidget = true;
      if (selectedGlobalFilter != null) {
        selectedGlobalFilter.filterSettings.forEach((d) => {
          const filterDetails = state.filters.find((f) => f.displayName === d.filter.displayName);
          if (!filterDetails) {
            isApplicableToWidget = false;
          }
        });
        if (isApplicableToWidget) {
          let isSavedFilter = false;
          const selectedIndex = state.savedFilters.findIndex(
            (f) => f.filterID === selectedGlobalFilter.filterID
          );
          if (selectedIndex > -1) {
            isSavedFilter = true;
          }
          const widgetGlobalFilter = isSavedFilter ? null : selectedGlobalFilter;
          dispatch({ widgetGlobalFilter: widgetGlobalFilter });
        } else {
          dispatch({ widgetGlobalFilter: null });
        }
      }
      dispatch((state) => ({
        selectedFilters: getAppliedGloalFilters(
          selectedGlobalFilter,
          state.filters,
          state.defaultFilter,
          isApplicableToWidget
        ),
      }));
    }
  }, [dispatch, selectedGlobalFilter, state.filters, state.defaultFilter, state.savedFilters]);

  return (
    <div className="hawkeye-filters-saved">
      {state.loading && (
        <AxiomLoader
          background={"rgba(255, 255, 255, 0.4)"}
          block=".HawkeyeV2Filters"
          fullScreen={true}
        />
      )}

      <HawkeyeV2StaticFilter defaultFilter={state.defaultFilter} openShowInfo={openShowInfo} />
      {selectedGlobalFilter != null && state.widgetGlobalFilter != null && (
        <HawkeyeV2StaticFilter
          defaultFilter={state.widgetGlobalFilter}
          openShowInfo={openShowInfo}
        />
      )}
      <HawkeyeV2SavedFilters
        savedFilters={state.savedFilters}
        selectedFilters={state.selectedFilters}
        applyFilter={applyFilter}
        editFilter={openEditFilter}
        openShowInfo={openShowInfo}
        deleteSavedFilter={deleteSavedFilter}
      />
      <div
        className="hawkeye-filter-tag hawkeye-filter-add"
        style={{ display: widget.allowedFilters == false ? "none" : "flex" }}
      >
        <button
          onClick={openCreateFilter}
          className="hawkeye-action hawkeye-filter-tag-name"
          disabled={state.filters.length == 0 ? "disabled" : ""}
        >
          <Icon icon="ICON_ADD" className="hawkeye-filter-add-icon" />
          {AxiomAPIs.translate("Create a filter")}
        </button>
      </div>
      <HawkeyeV2FilterPortal>
        <Fragment>
          {state.isCreatingFilter && isRightSideFilterPanelRequired == false && (
            <HawkeyeV2CreateFilter
              widget={widget}
              savedFilters={state.savedFilters}
              filterOptions={state.filters}
              smartFilterRefs={state.smartFilterRefs}
              saveFilter={saveFilter}
              updateSavedFilter={updateSavedFilter}
              closeCreateFilter={closeCreateFilter}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
              HawkeyeV2AppSettings={HawkeyeV2AppSettings}
              showPublicToggle={showPublicToggle}
            />
          )}
          {state.isCreatingFilter && isRightSideFilterPanelRequired == true && (
            <HawkeyeV2CreateFilterPanel
              widget={widget}
              savedFilters={state.savedFilters}
              filterOptions={state.filters}
              smartFilterRefs={state.smartFilterRefs}
              saveFilter={saveFilter}
              updateSavedFilter={updateSavedFilter}
              closeCreateFilter={closeCreateFilter}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID + "_" + "HawkeyeV2CreateFilterPanel"}
              HawkeyeV2AppSettings={HawkeyeV2AppSettings}
            />
          )}

          {state.isEditingFilter && isRightSideFilterPanelRequired == false && (
            <HawkeyeV2EditFilter
              widget={widget}
              editingFilter={state.editingFilter}
              filterOptions={state.filters}
              smartFilterRefs={state.smartFilterRefs}
              editFilter={editFilter}
              closeEditFilter={closeEditFilter}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
              HawkeyeV2AppSettings={HawkeyeV2AppSettings}
              showPublicToggle={showPublicToggle}
            />
          )}
          {state.isEditingFilter && isRightSideFilterPanelRequired == true && (
            <HawkeyeV2EditFilterPanel
              widget={widget}
              editingFilter={state.editingFilter}
              filterOptions={state.filters}
              smartFilterRefs={state.smartFilterRefs}
              editFilter={editFilter}
              closeEditFilter={closeEditFilter}
              appName={props.appName}
              appID={props.appID}
              componentID={props.componentID}
              HawkeyeV2AppSettings={HawkeyeV2AppSettings}
            />
          )}
          {state.isViewingInfo && (
            <HawkeyeV2FilterInfo
              infoFilter={state.infoFilter}
              openEditFilter={openEditFilter}
              closeShowInfo={closeShowInfo}
              enableEdit={state.enableEdit}
            />
          )}
        </Fragment>
      </HawkeyeV2FilterPortal>
    </div>
  );
}

export default memo(HawkeyeV2Filters);
